import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth, useTheme, useToast } from '../contexts/AppContexts';
import { Archive, Building, Copy, Edit, Plus, Shield, Trash2, Users, X } from 'lucide-react';
import { Button } from './ui/button';
import { API_BASE_URL } from '../config';
import AgencyBackupRestore from './AgencyBackupRestore';

const API = `${API_BASE_URL}/api`;

const AgencyManagement = ({ onClose }) => {
  const { theme } = useTheme();
  const { user: currentUser, token } = useAuth();
  const toast = useToast();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [backupTarget, setBackupTarget] = useState(undefined); // undefined=closed, null=all-agencies, {id,name}=single
  const [newAgency, setNewAgency] = useState({
    name: '',
    user_limit: 50,
    is_active: true
  });

  const isSuperAdmin = currentUser?.role === 'Super Admin';
  const isAgencyAdmin = currentUser?.role === 'Agency Admin';
  const hasAccess = isSuperAdmin || isAgencyAdmin;

  // Fetch agencies (only when the user actually has access)
  useEffect(() => {
    if (hasAccess) fetchAgencies();
  }, [hasAccess]);

  // Access control — Super Admin sees everything; Agency Admin sees only backup/restore for their own agency
  if (!hasAccess) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
        <div className="bg-gray-800 border border-red-500/30 p-8 rounded-lg max-w-md">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-8 w-8 text-red-400" />
            <h2 className="text-2xl font-bold text-red-400">Access Denied</h2>
          </div>
          <p className="text-gray-300 mb-6">
            You do not have permission to access Agency Management. Only Super Admins and Agency Admins can access this page.
          </p>
          <Button onClick={onClose} className="w-full bg-red-500 hover:bg-red-600">
            Close
          </Button>
        </div>
      </div>
    );
  }

  const fetchAgencies = async () => {
    setLoading(true);
    try {
      if (isAgencyAdmin && currentUser?.organization_id) {
        // Agency Admin: fetch only their own agency for backup/restore
        const response = await axios.get(`${API}/my-agency`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // /my-agency returns a compact shape — normalize into the row shape used below
        const row = response.data
          ? {
              id: response.data.id,
              name: response.data.name,
              registration_code: response.data.registration_code || '',
              is_active: true,
              user_count: 0,
              user_limit: 0,
            }
          : null;
        setAgencies(row ? [row] : []);
      } else {
        const response = await axios.get(`${API}/agencies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgencies(response.data);
      }
    } catch (error) {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to load agencies. ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgency = async () => {
    try {
      if (!newAgency.name.trim()) {
        toast.error('Agency name is required');
        return;
      }

      await axios.post(`${API}/agencies`, newAgency, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Agency created successfully!');
      setShowCreateModal(false);
      setNewAgency({ name: '', user_limit: 50, is_active: true });
      fetchAgencies();
    } catch (error) {
      console.error('Error creating agency:', error);
      toast.error('Failed to create agency. ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditAgency = (agency) => {
    setEditingAgency({
      id: agency.id,
      name: agency.name,
      user_limit: agency.user_limit,
      is_active: agency.is_active
    });
    setShowEditModal(true);
  };

  const handleSaveAgency = async () => {
    try {
      await axios.put(
        `${API}/agencies/${editingAgency.id}`,
        {
          name: editingAgency.name,
          user_limit: editingAgency.user_limit,
          is_active: editingAgency.is_active
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Agency updated successfully!');
      setShowEditModal(false);
      setEditingAgency(null);
      fetchAgencies();
    } catch (error) {
      console.error('Error updating agency:', error);
      toast.error('Failed to update agency. ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAgency = async (agencyId, agencyName) => {
    if (!window.confirm(`Are you sure you want to delete "${agencyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/agencies/${agencyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Agency deleted successfully');
      fetchAgencies();
    } catch (error) {
      console.error('Error deleting agency:', error);
      
      // Show detailed error message from backend
      const errorDetail = error.response?.data?.detail || error.message;
      
      // For constraint violations (400 errors), show a more prominent alert
      if (error.response?.status === 400) {
        // Show both toast and alert for better visibility
        toast.error(errorDetail, { duration: 6000 });
        setTimeout(() => {
          alert(`Cannot Delete Agency\n\n${errorDetail}\n\nPlease complete the required actions before deleting this agency.`);
        }, 100);
      } else {
        // For other errors, just show toast
        toast.error('Failed to delete agency. ' + errorDetail);
      }
    }
  };

  const copyToClipboard = (text) => {
    const onSuccess = () => toast.success('Registration code copied to clipboard!');
    const onError = () => toast.error('Failed to copy to clipboard');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(onSuccess).catch(onError);
    } else {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        onSuccess();
      } catch (e) {
        onError();
      }
    }
  };

  const filteredAgencies = agencies.filter(agency =>
    agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agency.registration_code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4">
      <div className={`${theme.colors.card} rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden relative z-[10000]`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <Building className="h-8 w-8 text-orange-400" />
            <h2 className="text-2xl font-bold text-white">Agency Management</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Controls */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search agencies or registration codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-400"
              />
            </div>
            {isSuperAdmin && (
              <Button
                onClick={() => setBackupTarget(null)}
                className="bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                data-testid="backup-all-btn"
              >
                <Archive className="h-4 w-4 mr-2" />
                Backup All Agencies
              </Button>
            )}
            {isSuperAdmin && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Agency
              </Button>
            )}
          </div>
        </div>

        {/* Agency List */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-400 border-t-transparent"></div>
              <p className="text-gray-400 mt-4">Loading agencies...</p>
            </div>
          ) : filteredAgencies.length === 0 ? (
            <div className="text-center py-12">
              <Building className="h-16 w-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                {isSuperAdmin
                  ? 'No agencies found'
                  : 'Your agency could not be loaded. Please contact a Super Admin.'}
              </p>
              {isSuperAdmin && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 bg-orange-500 hover:bg-orange-600"
                  data-testid="create-first-agency-btn"
                >
                  Create Your First Agency
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredAgencies.map(agency => (
                <div
                  key={agency.id}
                  className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-orange-400 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{agency.name}</h3>
                        {agency.is_active ? (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-gray-900 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-gray-400 text-sm">Registration Code</span>
                            <button
                              onClick={() => copyToClipboard(agency.registration_code)}
                              className="text-orange-400 hover:text-orange-300"
                              title="Copy code"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                          </div>
                          <code className="text-lg font-mono text-orange-400 font-bold">
                            {agency.registration_code}
                          </code>
                        </div>
                        
                        <div className="bg-gray-900 p-4 rounded-lg">
                          <span className="text-gray-400 text-sm block mb-2">User Status</span>
                          <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-orange-400" />
                            <span className="text-white text-lg font-bold">
                              {agency.user_count || 0} / {agency.user_limit}
                            </span>
                            <span className="text-gray-400 text-sm">users</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-2 ml-4">
                      <button
                        onClick={() => setBackupTarget({ id: agency.id, name: agency.name })}
                        className="p-2 bg-green-600 hover:bg-green-700 rounded-lg"
                        title="Backup & Restore"
                        data-testid={`backup-agency-${agency.id}`}
                      >
                        <Archive className="h-4 w-4 text-white" />
                      </button>
                      {isSuperAdmin && (
                        <>
                          <button
                            onClick={() => handleEditAgency(agency)}
                            className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg"
                            title="Edit agency"
                          >
                            <Edit className="h-4 w-4 text-white" />
                          </button>
                          <button
                            onClick={() => handleDeleteAgency(agency.id, agency.name)}
                            className="p-2 bg-red-500 hover:bg-red-600 rounded-lg"
                            title="Delete agency"
                          >
                            <Trash2 className="h-4 w-4 text-white" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Agency Modal */}
      {showCreateModal && isSuperAdmin && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md relative z-[10002]">
            <h3 className="text-xl font-bold text-white mb-4">Create New Agency</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Agency Name *</label>
                <input
                  type="text"
                  value={newAgency.name}
                  onChange={(e) => setNewAgency({ ...newAgency, name: e.target.value })}
                  placeholder="e.g., Fire Department Alpha"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">User Limit</label>
                <input
                  type="number"
                  value={newAgency.user_limit}
                  onChange={(e) => setNewAgency({ ...newAgency, user_limit: parseInt(e.target.value) || 50 })}
                  min="1"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active_create"
                  checked={newAgency.is_active}
                  onChange={(e) => setNewAgency({ ...newAgency, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_create" className="text-sm text-gray-400">
                  Agency Active
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewAgency({ name: '', user_limit: 50, is_active: true });
                }}
                className="flex-1 bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateAgency}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                Create Agency
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Agency Modal */}
      {showEditModal && editingAgency && isSuperAdmin && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/80 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md relative z-[10002]">
            <h3 className="text-xl font-bold text-white mb-4">Edit Agency</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Agency Name *</label>
                <input
                  type="text"
                  value={editingAgency.name}
                  onChange={(e) => setEditingAgency({ ...editingAgency, name: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">User Limit</label>
                <input
                  type="number"
                  value={editingAgency.user_limit}
                  onChange={(e) => setEditingAgency({ ...editingAgency, user_limit: parseInt(e.target.value) || 50 })}
                  min="1"
                  className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-orange-400"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active_edit"
                  checked={editingAgency.is_active}
                  onChange={(e) => setEditingAgency({ ...editingAgency, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active_edit" className="text-sm text-gray-400">
                  Agency Active
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <Button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAgency(null);
                }}
                className="flex-1 bg-gray-700 hover:bg-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveAgency}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Backup / Restore modal */}
      {backupTarget !== undefined && (
        <AgencyBackupRestore
          agency={backupTarget}
          onClose={() => setBackupTarget(undefined)}
        />
      )}
    </div>
  );
};

// User Management Component (Admin Only)
// ============================================================================


export default AgencyManagement;
